import React, { useEffect, useRef, useState } from "react";
import { X, Play, Pause, SkipBack, SkipForward, Mic, Headphones } from "lucide-react";

const audioUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/cheer-audio/${id}?part=cheer&auth=${localStorage.getItem("tycc_token")}`;
const when = (iso) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

// "Listen to your fans": plays every saved voice cheer for a child back to back.
const VoicePlaylist = ({ cheers, childName, onClose }) => {
  const list = cheers.filter((c) => c.voice);
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [pct, setPct] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const ref = useRef(null);
  const c = list[i];

  useEffect(() => {
    const a = ref.current;
    if (!a) return;
    a.load();
    a.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => { setPlaying(false); setBlocked(true); });
  }, [i]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!c) return null;
  const next = () => (i < list.length - 1 ? setI(i + 1) : onClose());
  const toggle = () => { const a = ref.current; if (playing) { a.pause(); setPlaying(false); } else a.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => setBlocked(true)); };

  return (
    <div className="fixed inset-0 z-[100] bg-[#0c4a6e]/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="voice-playlist" data-index={i}>
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8 text-center bubble-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <p className="font-bold text-[#7a1f45] inline-flex items-center gap-2"><Headphones size={18} className="text-[#e5356f]" /> {childName}'s fans</p>
          <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#fff0f5] text-gray-400 hover:text-[#e5356f] flex items-center justify-center" aria-label="Close" data-testid="voice-playlist-close"><X size={18} /></button>
        </div>
        <audio ref={ref} src={audioUrl(c.id)} preload="auto" onTimeUpdate={(e) => setPct(e.target.duration ? (e.target.currentTime / e.target.duration) * 100 : 0)} onEnded={() => { setPct(0); setTimeout(next, 600); }} onError={() => setTimeout(next, 800)} />
        <div className={`mx-auto mt-6 w-28 h-28 rounded-full bg-[#e5356f] text-white flex items-center justify-center text-4xl font-bold ${playing ? "voice-pulse" : ""}`} data-testid="voice-playlist-avatar">{c.author_name.charAt(0).toUpperCase()}</div>
        <p className="mt-4 text-xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }} data-testid="voice-playlist-author">{c.author_name}</p>
        <p className="mt-1 text-sm text-gray-600" data-testid="voice-playlist-text">“{c.text}” · {when(c.created_at)}</p>
        <div className="mt-5 h-2 rounded-full bg-[#fff0f5] overflow-hidden"><div className="h-full bg-[#e5356f] transition-[width] duration-150" style={{ width: `${pct}%` }} /></div>
        <p className="mt-2 text-xs text-gray-500 inline-flex items-center gap-1"><Mic size={12} /> Voice cheer {i + 1} of {list.length}{blocked && !playing ? " · tap play to start" : ""}</p>
        <div className="mt-4 flex items-center justify-center gap-4">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} className="w-11 h-11 rounded-full bg-[#fff0f5] text-[#e5356f] flex items-center justify-center disabled:opacity-40" aria-label="Previous" data-testid="voice-playlist-prev"><SkipBack size={18} /></button>
          <button onClick={toggle} className="w-16 h-16 rounded-full bg-[#e5356f] hover:bg-[#c92a5e] text-white flex items-center justify-center shadow-lg" aria-label={playing ? "Pause" : "Play"} data-testid="voice-playlist-toggle">{playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}</button>
          <button onClick={next} className="w-11 h-11 rounded-full bg-[#fff0f5] text-[#e5356f] flex items-center justify-center" aria-label="Next" data-testid="voice-playlist-next"><SkipForward size={18} /></button>
        </div>
      </div>
    </div>
  );
};

export default VoicePlaylist;
